import { useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { ERequestType } from "../../Enum/ERequestType"
import type { RodizioSettings } from "../../Objects/Rodizio/RodizioSettings"
import type { RodizioPlan } from "../../Objects/Rodizio/RodizioPlan"
import type { RodizioManagement } from "../../Objects/Rodizio/RodizioManagement"
import type { RodizioSession } from "../../Objects/Rodizio/RodizioSession"
import type { RodizioMetrics } from "../../Objects/Rodizio/RodizioMetrics"
import type { StartRodizioSession } from "../../Objects/Rodizio/StartRodizioSession"
import type { AddRodizioItems } from "../../Objects/Rodizio/AddRodizioItems"
import { DeleteRequest, GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"

const buildAddItemsRequest = (request: AddRodizioItems) => ({
  requestId: request.requestId,
  concurrencyToken: request.concurrencyToken,
  meals: request.meals.map((meal) => ({
    mealEntityId: meal.mealEntityId,
    description: meal.description,
    addtionals: meal.addtionals.map((additional) => ({
      addtionalEntityId: additional.addtionalEntityId,
      amount: additional.amount,
    })),
    selections: meal.selections.map((selection) => ({
      selectionEntityId: selection.selectionEntityId,
      selectionOptionEntityId: selection.selectionOptionEntityId,
    })),
  })),
  drinks: request.drinks.map((drink) => ({
    drinkEntityId: drink.drinkEntityId,
    description: drink.description,
    addtionals: drink.addtionals.map((additional) => ({
      addtionalEntityId: additional.addtionalEntityId,
      amount: additional.amount,
    })),
    selections: drink.selections.map((selection) => ({
      selectionEntityId: selection.selectionEntityId,
      selectionOptionEntityId: selection.selectionOptionEntityId,
    })),
  })),
})

export const useRodizio = () => {
  const { t } = useTranslation("general")

  const { setError } = useError()

  const [isLoading, setIsLoading] = useState(false)

  const run = async <T>(operation: () => Promise<T>) => {
    setIsLoading(true)

    try {
      return await operation()
    } catch (requestError) {
      const message =
        requestError instanceof Error && requestError.message.trim()
          ? requestError.message
          : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  const unwrap = async <T>(request: Promise<{ success: boolean; message: string; data?: T }>) => {
    const response = await request

    if (!response.success) throw new Error(response.message)

    return response.data
  }

  const getManagement = () => run(() => unwrap(GetRequest<RodizioManagement>(API_ROUTES.RODIZIO.MANAGEMENT)))

  const getOperationConfiguration = () =>
    run(() => unwrap(GetRequest<RodizioManagement>(API_ROUTES.RODIZIO.OPERATION_CONFIGURATION)))

  const getMetrics = (startDate: string, endDate: string) =>
    run(() => unwrap(GetRequest<RodizioMetrics>(API_ROUTES.RODIZIO.METRICS(startDate, endDate))))

  const saveSettings = (settings: RodizioSettings) =>
    run(() => unwrap(PutRequest<RodizioSettings>(API_ROUTES.RODIZIO.SETTINGS, settings)))

  const savePlan = (plan: RodizioPlan) => run(() => unwrap(PutRequest<RodizioPlan>(API_ROUTES.RODIZIO.PLANS, plan)))

  const setPlanStatus = (planId: number, isActive: boolean) =>
    run(() => unwrap(PutRequest<RodizioPlan>(API_ROUTES.RODIZIO.PLAN_STATUS(planId), { isActive })))

  const removePlan = (planId: number) =>
    run(async () => {
      await unwrap(DeleteRequest<void>(API_ROUTES.RODIZIO.PLAN(planId)))
      return true
    })

  const getOrderSessions = (orderId: number) =>
    run(async () => (await unwrap(GetRequest<RodizioSession[]>(API_ROUTES.RODIZIO.ORDER_SESSIONS(orderId)))) ?? [])

  const startSession = (request: StartRodizioSession) =>
    run(() => unwrap(PostRequest<RodizioSession>(API_ROUTES.RODIZIO.START_SESSION, request)))

  const finishSession = (sessionId: number, concurrencyToken: string, isCancellation = false) =>
    run(async () => {
      await unwrap(
        PostRequest<void>(API_ROUTES.RODIZIO.FINISH_SESSION(sessionId), { concurrencyToken, isCancellation }),
      )
      return true
    })

  const finishAllActiveSessions = () =>
    run(() => unwrap(PostRequest<number>(API_ROUTES.RODIZIO.FINISH_ALL_SESSIONS, {})))

  const addWaiterItems = (sessionId: number, request: AddRodizioItems) =>
    run(() =>
      unwrap(PostRequest<RodizioSession>(API_ROUTES.RODIZIO.WAITER_ITEMS(sessionId), buildAddItemsRequest(request))),
    )

  const getPublicConfiguration = (slug: string) =>
    run(() => unwrap(GetRequest<RodizioManagement>(API_ROUTES.RODIZIO.PUBLIC_CONFIGURATION(slug))))

  const getTableSessions = (slug: string) =>
    run(
      async () =>
        (await unwrap(
          GetRequest<RodizioSession[]>(API_ROUTES.RODIZIO.TABLE_SESSIONS(slug), ERequestType.TableSelfService),
        )) ?? [],
    )

  const getCustomerSession = (slug: string) =>
    run(() =>
      unwrap(GetRequest<RodizioSession | null>(API_ROUTES.RODIZIO.CUSTOMER_SESSION(slug), ERequestType.Customer)),
    )

  const joinCustomerSession = (slug: string, accessCode: string) =>
    run(() =>
      unwrap(
        PostRequest<RodizioSession>(API_ROUTES.RODIZIO.CUSTOMER_JOIN(slug), { accessCode }, ERequestType.Customer),
      ),
    )

  const addPublicItems = (slug: string, sessionId: number, request: AddRodizioItems, isTablet: boolean) =>
    run(() =>
      unwrap(
        PostRequest<RodizioSession>(
          isTablet
            ? API_ROUTES.RODIZIO.TABLE_ITEMS(slug, sessionId)
            : API_ROUTES.RODIZIO.CUSTOMER_ITEMS(slug, sessionId),
          buildAddItemsRequest(request),
          isTablet ? ERequestType.TableSelfService : ERequestType.Customer,
        ),
      ),
    )

  return {
    isLoading,
    getManagement,
    getOperationConfiguration,
    getMetrics,
    saveSettings,
    savePlan,
    setPlanStatus,
    removePlan,
    getOrderSessions,
    startSession,
    finishSession,
    finishAllActiveSessions,
    addWaiterItems,
    getPublicConfiguration,
    getTableSessions,
    getCustomerSession,
    joinCustomerSession,
    addPublicItems,
  }
}
